import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Header from "@/components/Header";
import PublicFooter from "@/components/PublicFooter";
import { ArrowLeft, Clock, BookOpen, CheckCircle, ArrowRight } from "lucide-react";

const InterviewPreparationGuidePage = () => {
  return (
    <>
      <Helmet>
        <title>Complete Interview Preparation Guide 2025 | Hireun</title>
        <meta name="description" content="Master interview preparation with our comprehensive guide. Learn research techniques, answer frameworks, and strategies to ace any interview." />
        <meta name="keywords" content="interview preparation, how to prepare for interview, interview tips, job interview guide, interview strategies" />
        <link rel="canonical" href="https://hireun.com/guides/interview-preparation" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

        {/* Article Header */}
        <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link to="/guides" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Guides
          </Link>

          <div className="mb-8">
            <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
              Fundamentals
            </span>
            <div className="flex items-center text-sm text-gray-500 mt-4">
              <Clock className="w-4 h-4 mr-1" />
              <span>12 min read</span>
              <span className="mx-2">•</span>
              <span>Updated January 2025</span>
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Complete Interview Preparation Guide: From Research to Success
          </h1>

          <p className="text-xl text-gray-600 mb-12 leading-relaxed">
            Interview preparation is not about memorizing answers — it's about strategic alignment between 
            your experience and the role's requirements. This comprehensive guide will transform how you 
            approach every interview, regardless of your experience level or industry.
          </p>

          {/* Table of Contents */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-12">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BookOpen className="w-5 h-5 mr-2 text-blue-600" />
              In This Guide
            </h2>
            <ul className="space-y-2">
              <li><a href="#foundation" className="text-blue-600 hover:underline">1. Building Your Interview Foundation</a></li>
              <li><a href="#research" className="text-blue-600 hover:underline">2. Strategic Company Research</a></li>
              <li><a href="#stories" className="text-blue-600 hover:underline">3. Crafting Your Professional Stories</a></li>
              <li><a href="#questions" className="text-blue-600 hover:underline">4. Preparing for Common Questions</a></li>
              <li><a href="#star" className="text-blue-600 hover:underline">5. Mastering the STAR Method</a></li>
              <li><a href="#day-of" className="text-blue-600 hover:underline">6. Interview Day Execution</a></li>
              <li><a href="#follow-up" className="text-blue-600 hover:underline">7. Post-Interview Strategy</a></li>
            </ul>
          </div>

          {/* Main Content */}
          <div className="prose prose-lg max-w-none">
            <section id="foundation" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                1. Building Your Interview Foundation
              </h2>
              <p className="text-gray-700 mb-4">
                Before you can excel in interviews, you need to understand what hiring managers are actually 
                evaluating. Contrary to popular belief, they're not just assessing your skills — they're 
                determining whether you can solve their specific problems and fit within their team culture.
              </p>
              <p className="text-gray-700 mb-4">
                The foundation of great interview preparation starts with self-awareness. You need to clearly 
                articulate your professional value proposition: what makes you uniquely qualified for this 
                specific role, and how your experience translates to the challenges the company faces.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-600 p-4 my-6">
                <p className="text-blue-800 font-medium">
                  Key Insight: Interviewers make decisions based on evidence, not claims. Every statement 
                  you make should be backed by a specific example from your experience.
                </p>
              </div>
              <p className="text-gray-700 mb-4">
                Start by creating an inventory of your professional achievements. List every significant 
                project, initiative, or improvement you've contributed to. For each item, note the context, 
                your specific actions, and the measurable outcomes. This inventory becomes your source 
                material for interview responses.
              </p>
            </section>

            <section id="research" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                2. Strategic Company Research
              </h2>
              <p className="text-gray-700 mb-4">
                Surface-level research is not enough. Reading the company's "About Us" page puts you at the 
                same level as every other candidate. Strategic research means understanding the company's 
                challenges, competitive position, and strategic priorities.
              </p>
              <p className="text-gray-700 mb-4">
                Start with recent news articles and press releases to understand current initiatives. Review 
                the company's financial reports if publicly available — they reveal strategic priorities and 
                pain points. Study the LinkedIn profiles of people in the role and department you're 
                interviewing for to understand career paths and team composition.
              </p>
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Research Checklist:</h3>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Company mission, values, and recent strategic announcements</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Competitive landscape and market position</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Recent product launches or company changes</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Team structure and hiring manager background</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <span>Company culture indicators from employee reviews</span>
                </li>
              </ul>
            </section>

            <section id="stories" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                3. Crafting Your Professional Stories
              </h2>
              <p className="text-gray-700 mb-4">
                Interviews are storytelling exercises. The candidates who succeed are those who can transform 
                their experience into compelling narratives that demonstrate their value. Your stories should 
                illustrate not just what you did, but how you think and approach challenges.
              </p>
              <p className="text-gray-700 mb-4">
                Prepare 5-7 core stories that cover different competencies: leadership, problem-solving, 
                collaboration, handling failure, and driving results. Each story should be adaptable to 
                different questions — the same project might illustrate your analytical skills for one 
                question and your stakeholder management for another.
              </p>
              <p className="text-gray-700 mb-4">
                When crafting stories, focus on specificity. Vague statements like "I improved the process" 
                carry little weight. Instead, describe exactly what was broken, what you changed, and what 
                the measurable impact was. Numbers and percentages make your achievements concrete and credible.
              </p>
            </section>

            <section id="questions" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                4. Preparing for Common Questions
              </h2>
              <p className="text-gray-700 mb-4">
                While every interview is unique, certain questions appear consistently. Rather than memorizing 
                scripted answers, develop frameworks for approaching these questions that you can adapt in 
                real-time.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">"Tell me about yourself"</h3>
              <p className="text-gray-700 mb-4">
                This isn't an invitation to recite your resume. It's your opportunity to frame the conversation 
                around your most relevant qualifications. Structure your response as: present situation → 
                key past experience → why this role. Keep it under 2 minutes and end with a clear connection 
                to the role you're interviewing for.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">"Why do you want this role?"</h3>
              <p className="text-gray-700 mb-4">
                Your answer should demonstrate genuine interest backed by research. Connect your career 
                trajectory to the company's direction. Show that you understand what the role involves and 
                explain specifically why it aligns with your professional goals.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">"What's your greatest weakness?"</h3>
              <p className="text-gray-700 mb-4">
                Avoid clichéd "humble brags" like "I work too hard." Choose a genuine developmental area 
                that won't disqualify you for the role, and focus most of your answer on specific steps 
                you're taking to improve.
              </p>
            </section>

            <section id="star" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                5. Mastering the STAR Method
              </h2>
              <p className="text-gray-700 mb-4">
                The STAR method (Situation, Task, Action, Result) is the gold standard for behavioral 
                interview responses. However, most candidates use it incorrectly by spending too much time 
                on Situation and Task, leaving insufficient time for Action and Result — the parts 
                interviewers care most about.
              </p>
              <div className="bg-gray-100 rounded-lg p-6 my-6">
                <h4 className="font-semibold text-gray-900 mb-3">Optimal STAR Distribution:</h4>
                <ul className="space-y-2">
                  <li><strong>Situation (10-15%):</strong> Brief context setting</li>
                  <li><strong>Task (10-15%):</strong> Your specific responsibility</li>
                  <li><strong>Action (50-60%):</strong> Detailed steps you took</li>
                  <li><strong>Result (20-25%):</strong> Measurable outcomes and learnings</li>
                </ul>
              </div>
              <p className="text-gray-700 mb-4">
                Practice your STAR stories out loud. Time yourself to ensure you're not rambling. A strong 
                behavioral response should take 2-3 minutes. If you find yourself going longer, you're 
                probably including unnecessary detail.
              </p>
            </section>

            <section id="day-of" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                6. Interview Day Execution
              </h2>
              <p className="text-gray-700 mb-4">
                The best preparation is worthless if you can't execute under pressure. Interview day requires 
                managing your physical state, environment, and mindset.
              </p>
              <p className="text-gray-700 mb-4">
                For virtual interviews, test your technology 24 hours in advance. Ensure your lighting is 
                flattering, your background is professional, and your internet connection is stable. Have 
                a backup plan — know the phone number to call if video fails.
              </p>
              <p className="text-gray-700 mb-4">
                For in-person interviews, arrive 10-15 minutes early. Use that time to observe the office 
                environment and calm your nerves. Bring copies of your resume and a notebook — taking brief 
                notes shows engagement.
              </p>
              <div className="bg-green-50 border-l-4 border-green-600 p-4 my-6">
                <p className="text-green-800 font-medium">
                  Pro Tip: The interview starts the moment you enter the building. Be courteous to everyone 
                  you meet — receptionists' opinions are often solicited after interviews.
                </p>
              </div>
            </section>

            <section id="follow-up" className="mb-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                7. Post-Interview Strategy
              </h2>
              <p className="text-gray-700 mb-4">
                Your interview doesn't end when you leave the room. A thoughtful follow-up can differentiate 
                you from equally qualified candidates.
              </p>
              <p className="text-gray-700 mb-4">
                Send a thank-you email within 24 hours. Don't just express gratitude — reference specific 
                conversation points and reiterate your interest with new insight. If you discussed a 
                particular challenge, include a brief thought on how you'd approach it.
              </p>
              <p className="text-gray-700 mb-4">
                After sending your thank-you note, resist the urge to follow up repeatedly. One check-in 
                after a week is acceptable if you haven't heard back. Beyond that, frequent follow-ups 
                can signal desperation.
              </p>
            </section>
          </div>

          {/* CTA Box */}
          <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-8 mt-12 text-white">
            <h3 className="text-2xl font-bold mb-4">
              Get Personalized Interview Preparation
            </h3>
            <p className="text-blue-100 mb-6">
              This guide provides the framework. Hireun provides personalized preparation tailored to 
              your specific role, experience, and target company. Upload your CV and job description 
              to receive customized interview questions and answers.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center bg-white text-blue-600 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            >
              Try Hireun Free
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>

          {/* Related Guides */}
          <div className="mt-12">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Related Guides</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/guides/job-description-analysis" className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-1">How to Analyze a Job Description</h4>
                <p className="text-sm text-gray-600">Learn to decode job descriptions like a recruiter.</p>
              </Link>
              <Link to="/guides/senior-interview-questions" className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <h4 className="font-semibold text-gray-900 mb-1">Senior Interview Questions</h4>
                <p className="text-sm text-gray-600">Strategies for executive and leadership interviews.</p>
              </Link>
            </div>
          </div>
        </article>

        <PublicFooter />
      </div>
    </>
  );
};

export default InterviewPreparationGuidePage;
